import { Format, Immutable, NumberFormatter, PercentageFormatter } from '@zervant/reactcomponents'
import { TABS } from 'client/components/Modal/CompanyInformationModal/CompanyInformationModal'
import { PayNowHelper } from 'client/components/paynow/Paynow.helper'
import { Segment } from 'client/Global/Segment'
import { SegmentEvents } from 'client/Global/SegmentEvents'
import { TextLineInterface } from 'client/Interfaces/Draft/TextLineInterface'
import { ContactType, ExternalTaxRatesInterface, TaxRuleInterface } from 'client/Interfaces/ExternalVATRatesInterface'
import {
  PayNowAuthorizationFailure,
  PayNowAuthorizationSucess,
  PayNowInterface,
} from 'client/Interfaces/PayNow/AccountAuthorizationParams'
import { l, tsxl } from 'client/Other/Translation'
import { CustomSettingsState } from 'client/reducers/customSettings'
import { ExperimentsState } from 'client/reducers/experiments'
import isEmpty from 'lodash/isEmpty'
import pick from 'lodash/pick'
import * as React from 'react'

import { defaultLine } from '../../actions/helpers/draft'
import { getDefaultRecurringProfile } from '../../actions/helpers/recurringProfile'
import { Analytics, checkPlacement } from '../../Global/Analytics'
import { AnalyticsType, Category, Events, Placement, Result, trackingFields } from '../../Global/AnalyticsEvents'
import { Container } from '../../Global/Container'
import { AddressInterface } from '../../Interfaces/AddressInterface'
import { CollectionInterface } from '../../Interfaces/CollectionInterface'
import { CustomerInterface } from '../../Interfaces/CustomerInterface'
import { DiscountLocation, DiscountState, DiscountType, DiscountVAT } from '../../Interfaces/DiscountSettingsInterface'
import { DocumentTypes, DraftInterface, InvoiceTypes } from '../../Interfaces/Draft/DraftInterface'
import { InvoiceItemType } from '../../Interfaces/Draft/DraftItemType'
import { FieldInterface } from '../../Interfaces/Draft/FieldInterface'
import { DraftFieldsInterface } from '../../Interfaces/Draft/FieldsInterface'
import { LineInterface } from '../../Interfaces/Draft/LineInterface'
import { ExchangeRateInterface } from '../../Interfaces/ExchangeRateInterface'
import { LabelKeys } from '../../Interfaces/LabelsInterface'
import { OrderDraftInterface } from '../../Interfaces/OrderInterface'
import { ProductTypesEnum } from '../../Interfaces/ProductInterface'
import {
  RecurringProfileSettingsInterface,
  RecurringProfileStatusType,
} from '../../Interfaces/RecurringProfileInterface'
import { SchemaInterface } from '../../Interfaces/Schema/SchemaInterface'
import { ModelInterface } from '../../Interfaces/Settings/ModelInterface'
import { SettingsInterface } from '../../Interfaces/Settings/SettingsInterface'
import { QuotaTypes, SubscriptionInterface } from '../../Interfaces/Subscription'
import { TokenInterface } from '../../Interfaces/TokenInterface'
import { areWithinFrenchTerritories, countriesWithinEU } from '../../Other/Countries'
import { formatDate, transformDateFormatForMoment } from '../../Other/functions/dateFunctions'
import { convertPercentageFromLocale } from '../../Other/functions/discountFunction'
import { isValidPaymentMethod } from '../../Other/functions/documentFunctions'
import { getAllowedVatRatesMap } from '../../Other/functions/externalTaxRateFunctions'
import { getDefaultDepositOrder, getDefaultScheduleOrder } from '../../Other/functions/paymentScheduleFunctions'
import { convertPctFromLocale } from '../../Other/functions/vatFunctions'
import { Type } from '../../Other/Type'
import { doesCurrentPlanSupportFeature } from '../../reducers/Subscription/companySubscription'
import Button, { ButtonType } from '../common/Button/Button'
import CheckBox from '../common/CheckBox/CheckBox'
import { Document } from '../common/Document/Document'
import { FieldCreator } from '../common/FieldCreator'
import Input from '../common/Input/Input'
import { Layout, LayoutColumn } from '../common/Layout/Layout'
import { NumberInput } from '../common/NumberInput'
import { CustomerSelector } from '../CustomerSelector/CustomerSelector'
import keys from '../txtkeys/recurringProfileKeys'
import { DiscountSettings } from './DiscountSettings/DiscountSettings'
import IBANSelector from './IBANSelector'
import { InvoiceEditorHeading } from './InvoiceEditorHeading/InvoiceEditorHeading'
import { InvoiceHeader } from './InvoiceHeader/InvoiceHeader'
import { InvoiceItems } from './InvoiceItems'
import InvoiceSettings from './InvoiceSettings/InvoiceSettings'
import { PaymentScheduleSettings, PaymentScheduleType } from './PaymentScheduleSettings/PaymentScheduleSettings'
import { RecurringProfileSettings } from './RecurringProfilesSettings/RecurringProfileSettings'
import { SubtotalLine } from './SubtotalLine'
import { InvoiceTextLine } from './TextLine'
import { InvoiceTitleLine } from './TitleLine'

export interface IEStateProps {
  subscription?: SubscriptionInterface
  drafts?: CollectionInterface<DraftInterface>
  draft?: DraftInterface
  recurringProfile?: RecurringProfileSettingsInterface
  settings?: SettingsInterface
  token?: TokenInterface
  language?: string
  config?: any
  customers?: CollectionInterface<CustomerInterface>
  automation?: string
  company?: any
  products?: any
  customSettings?: CustomSettingsState
  experiments?: ExperimentsState
  hasRightsToPaymentSchedules?: boolean
  hasRightsToRecurringProfile?: boolean
  isRecurringProfile?: boolean
  selectMaps?: {
    tradeNames: { [key: string]: string }
    templates: { [key: string]: string }
    currencies: { [key: string]: string }
    languages: { [key: string]: string }
    columnNames: { [key: string]: string }
    invoiceTypes: { [key: string]: string }
  }
  internal?: {
    model: ModelInterface
    language: string
    template: string
    visibleColumns: string[]
    lineColumns: string[]
    schema: SchemaInterface
    taxFree: boolean
    currencySymbol: string
    defaultCurrency: string
  }
  exchangeRate?: ExchangeRateInterface
  ongoing?: any
  payNow: PayNowInterface
  authorizationFailure?: PayNowAuthorizationFailure
  authorizationSuccess?: PayNowAuthorizationSucess
  location?: {
    search: string
  }
  origin?: string
  externalVATRates: ExternalTaxRatesInterface
  isAgerasNextMigration: boolean
}

export interface IEExternalActions {
  initDraft?: () => void
  getCustomers?: () => void
  getCompany?: () => void
  getProducts?: () => Promise<void>
  getCustomSettings?: () => void
  getExperiments?: () => void
  getConfigs?: () => void
  getAccounts?: () => void
  getSettings?: () => void
  getFreetexts?: () => void
  getTranslations?: (lang: string) => void
  changeFields?: (fields: DraftFieldsInterface) => void
  changeStyles?: (settings: SettingsInterface) => void
  changeCustomer?: (customer: any) => void
  changeLines?: (lines: InvoiceItemType[]) => void
  changeDraft?: (draft: DraftInterface) => void
  previewDraft?: (draft: DraftInterface) => void
  previewRecurringProfile?: (draft: DraftInterface, profile: RecurringProfileSettingsInterface) => void
  draftSaveAndClose?: (draft: DraftInterface) => void
  getExchangeRate?: () => void
  openCustomerModal?: (customer: CustomerInterface, customerName) => void
  onDocumentNumberEdit?: () => void
  closeEditor?: () => void
  openDownpaymentModal?: () => void
  openPaymentScheduleModal?: () => void
  openRecurringProfileIntermediaryModal: () => void
  openProductModal?: (index, context?: string) => void
  openDefaultProductTypeModal?: (index, onClose) => void
  openVATOptions?: (afterCreateNew) => void
  openUnitOptions?: (afterCreateNew) => void
  openPaymentTermsModal?: (afterCreateNew) => void
  openLatePaymentInterestModal?: (afterCreateNew) => void
  updateOrder?: (order: OrderDraftInterface) => void
  deleteOrder?: () => void
  navigateToInvoicingSettings?: () => void
  navigateToCompanySettings?: (draft: DraftInterface, activeTab: TABS, TradenameId?: string) => void
  updateRecurringProfileSettings?: (profile: RecurringProfileSettingsInterface) => void
  deleteRecurringProfileSettings?: () => void
  updatePaymentMeans?: (draft: DraftInterface, ibanFields) => void
  openPayNowFailureModal: () => void
  openPayNowSuccessModal: () => void
  saveAuthorization: (authorizationParams: PayNowAuthorizationSucess) => Promise<any>
  loadAuthorizationStatus: () => Promise<any>
  getExternalVATRates: () => Promise<ExternalTaxRatesInterface>
}

export type InvoiceEditorPropsInterface = IEStateProps & IEExternalActions

const CUSTOMER_TYPE_TO_CONTACT_TYPE = {
  p: 'person' as ContactType,
  c: 'company' as ContactType,
}

export class InvoiceEditor extends Container<InvoiceEditorPropsInterface> {
  private currencyF: NumberFormatter
  private exchangeRateF: NumberFormatter

  constructor(props) {
    super(props)

    this.props.getFreetexts()
    this.props.getTranslations(props.token.locale)
    this.props.getTranslations(props.token.locale)
    this.state = {
      highlightCustomerSelection: true,
      isVisibleExchangeRate: false,
      orderType:
        this.props.draft.order && this.props.draft.order.orderType.startsWith('schedule') ? 'schedule' : 'downPayment',
    }
    this.handleCurrencyChange = this.handleCurrencyChange.bind(this)
    this.handleExchangeRateChange = this.handleExchangeRateChange.bind(this)
  }

  controller = new AbortController()

  private isVATLessSales() {
    const vatlessType = 127
    const value = Number(this.props.draft.fields.type.value)
    return value === vatlessType
  }

  public async componentDidMount() {
    const {
      draft,
      exchangeRate,
      changeDraft,
      internal: { defaultCurrency },
      isAgerasNextMigration,
      externalVATRates,
    } = this.props
    const { fields } = draft
    const selectedCurrency = fields.currency.value
    const shouldRenderRate = selectedCurrency !== defaultCurrency

    if (isAgerasNextMigration) {
      this.props.getExternalVATRates()
    }

    await this.props.getProducts() // get fresh products when component mounts

    if (shouldRenderRate) {
      changeDraft({
        ...draft,
        isDirty: false,
        fields: {
          ...fields,
          exchangeRate: {
            ...fields.exchangeRate,
            value: exchangeRate[selectedCurrency] / exchangeRate[defaultCurrency],
          },
        },
      })
      this.setState({
        isVisibleExchangeRate: true,
      })
    }

    const {
      authorizationFailure,
      authorizationSuccess,
      saveAuthorization,
      openPayNowFailureModal,
      loadAuthorizationStatus,
    } = this.props
    if (authorizationFailure) {
      Analytics.trackEvent(Events.olpSignup, {
        type: AnalyticsType.action,
        category: Category.onlinePayment,
        result: Result.cancel,
        placement: checkPlacement(window.location.pathname),
      })
      openPayNowFailureModal()
    } else if (authorizationSuccess) {
      Analytics.trackEvent(Events.olpSignup, {
        type: AnalyticsType.action,
        category: Category.onlinePayment,
        result: Result.success,
        placement: checkPlacement(window.location.pathname),
      })
      if (PayNowHelper.validateAndConsumeCSRFToken(authorizationSuccess.state)) {
        void saveAuthorization(authorizationSuccess).then(() => {
          if (this.props.payNow.status.connected) {
            this.props.openPayNowSuccessModal()
          } else {
            openPayNowFailureModal()
          }
        })
      } else {
        openPayNowFailureModal()
      }
    } else {
      void loadAuthorizationStatus()
    }

    if (isEmpty(externalVATRates?.rules) && isEmpty(externalVATRates?.taxRates)) {
      Segment.track(SegmentEvents.accounting.vatErrorUserSide)
    }
  }

  public componentDidUpdate(prevProps) {
    if (
      this.props.company?.agerasNextMigration &&
      this.props.draft?.receiver?.address?.country !== prevProps.draft?.receiver?.address?.country
    ) {
      this.updateVatRate()
    }
  }

  public UNSAFE_componentWillReceiveProps(props) {
    if (
      this.props.draft?.summary?.reductionTotal !== props.draft?.summary?.reductionTotal &&
      this.props?.draft?.order
    ) {
      const type =
        this.props?.draft?.order?.orderType.startsWith('downPayment') ||
        (!this.props?.draft?.order && this.state.orderType === 'downPayment')
          ? 'downPayment'
          : 'schedule'
      this.setDefaultOrder(type, props.draft)
    }
    this.props = props

    if (props.token && props.internal && props.internal.currencySymbol) {
      this.currencyF = new NumberFormatter(Format.fromLocale(props.token.locale), props.internal.currencySymbol)
    }

    if (props.token && props.internal) {
      this.exchangeRateF = new NumberFormatter(Format.fromLocale(props.token.locale))
    }

    // TODO: Because ACTIONS.LOAD_VITALS is called before rendering InvoiceEditor, config will always be defined
    //      Later when LOAD_VITALS eventually gets refactored, this will become useful.
    if (!this.props.config && !this.props.ongoing.GET_CONFIG) {
      this.props.getConfigs()
      return
    }

    if (!this.props.company && !this.props.ongoing.GET_COMPANY) {
      this.props.getCompany()
      return
    }

    if (this.props.customSettings?.loaded === false && !this.props.ongoing.LOAD_CUSTOM_SETTINGS) {
      this.props.getCustomSettings()
      return
    }

    if (this.props.experiments?.loaded === false && !this.props.ongoing.LOAD_EXPERIMENTS) {
      this.props.getExperiments()
      return
    }

    // TODO: Because ACTIONS.LOAD_VITALS is called before rendering InvoiceEditor, customers will always be defined
    //      Later when LOAD_VITALS eventually gets refactored, this will become useful.
    if (!this.props.customers && !this.props.ongoing.GET_CUSTOMERS) {
      this.props.getCustomers()
      return
    }

    if (!this.props.products && !this.props.ongoing.GET_PRODUCTS) {
      this.props.getProducts()
      return
    }

    // TODO: Because ACTIONS.LOAD_VITALS is called before rendering InvoiceEditor, products will always be defined
    //      Later when LOAD_VITALS eventually gets refactored, this will become useful.
    if (!this.props.exchangeRate && !this.props.ongoing.GET_EXCHANGE_RATE) {
      this.props.getExchangeRate()
    }
  }

  public componentWillUnmount() {
    this.controller.abort()
  }

  private getDefaultLineValues() {
    const defaultLineValues = defaultLine(this.props.settings, this.props.externalVATRates?.taxRates)

    if (!this.props.company?.agerasNextMigration) {
      return defaultLineValues
    }

    const { productType, vatRateId: defaultVatRateId } = defaultLineValues
    const vatRateId = this.getVatRateFromRules(productType, defaultVatRateId)

    if (vatRateId) {
      const vatRate = this.props.externalVATRates?.taxRates.find((rate) => rate.id === vatRateId)

      if (vatRate) {
        defaultLineValues.vatRateId = vatRateId
      }
    }

    return defaultLineValues
  }

  private getVatRateFromRules(productType: string, defaultVatRateId?: string) {
    const { externalVATRates, company, draft } = this.props
    const rules = externalVATRates?.rules
    const companyCountry = company?.country
    const receiverCountry = draft?.receiver?.address?.country
    const contactType = CUSTOMER_TYPE_TO_CONTACT_TYPE[draft?.receiver?.customerType || 'c']

    if (!productType) {
      return
    }

    let region = 'world'

    if (receiverCountry) {
      if (receiverCountry === companyCountry || areWithinFrenchTerritories(companyCountry, receiverCountry)) {
        region = 'local'
      } else if (countriesWithinEU.includes(receiverCountry)) {
        region = 'eu'
      }
    }

    const filteredRules = rules
      .filter((rule: TaxRuleInterface) => {
        return (
          (rule.contactType === contactType || rule.contactType === 'any') &&
          (rule.productType === productType.toLowerCase() || rule.productType === 'any') &&
          rule.region === region
        )
      })
      .sort((a, b) => a.priority - b.priority)

    if (filteredRules.length === 0) {
      return
    }

    const isDefaultVatRateIdCompliantWithRules = !!filteredRules.find((rule) => rule?.taxRateId === defaultVatRateId)

    if (isDefaultVatRateIdCompliantWithRules) {
      return defaultVatRateId
    }

    return filteredRules[0].taxRateId
  }

  private shouldSetVatRateFromRules(productType?: string, vatRateId?: string) {
    if (!vatRateId) {
      return true
    }

    const vatPctMapIds = Object.keys(
      getAllowedVatRatesMap(
        this.props.externalVATRates,
        this.props.draft?.receiver?.address?.country,
        this.props.token?.locale,
        productType,
        this.props.draft?.receiver?.customerType &&
          CUSTOMER_TYPE_TO_CONTACT_TYPE[this.props.draft?.receiver?.customerType],
      ),
    )

    return !vatPctMapIds.includes(vatRateId)
  }

  private updateVatRate() {
    if (!this.props.draft.lines) {
      return
    }

    const lines: InvoiceItemType[] = Immutable.copy(this.props.draft.lines)
    let updated = false

    lines.forEach((line, index) => {
      if (line.type === 'row') {
        const { productType } = line

        if (this.shouldSetVatRateFromRules(productType, line.vatRateId)) {
          const vatRateId = this.getVatRateFromRules(productType)

          if (vatRateId) {
            line.vatRateId = vatRateId
            updated = true
          }
        }
      }
    })

    if (updated) {
      this.props.changeLines?.(lines)
    }
  }

  private handleCurrencyChange(selectedCurrency: string): void {
    const {
      draft: { fields },
      exchangeRate,
      changeFields,
      internal: { defaultCurrency },
    } = this.props

    if (fields.currency.value === selectedCurrency) {
      return
    }

    if (selectedCurrency === null) {
      selectedCurrency = fields.currency.value
    }

    const shouldRenderRate = selectedCurrency !== defaultCurrency

    changeFields?.({
      ...fields,
      currency: {
        ...fields.currency,
        value: selectedCurrency,
      },
      exchangeRate: {
        ...fields.exchangeRate,
        value: shouldRenderRate ? exchangeRate[selectedCurrency] / exchangeRate[defaultCurrency] : null,
      },
    })

    this.setState({
      isVisibleExchangeRate: shouldRenderRate,
    })
  }

  private handleExchangeRateChange(valueChange: number): void {
    const {
      draft: { fields },
      changeFields,
    } = this.props
    changeFields?.({
      ...fields,
      exchangeRate: {
        ...fields.exchangeRate,
        value: valueChange !== 0 ? valueChange : fields.exchangeRate.value,
      },
    })
  }

  private getProductTypeFromSettings() {
    const invoiceSettings = this.props.settings.models.find((model) => model.name === 'models.invoice.sales')

    if (!invoiceSettings || !invoiceSettings.fields || !Array.isArray(invoiceSettings.fields)) {
      return
    }

    const productTypeSetting = invoiceSettings.fields.find((field) => field.name === 'productType')

    return productTypeSetting && productTypeSetting.default
  }

  private isEditingApprovedInvoice(): boolean {
    return Boolean(this.props.draft.editInvoice)
  }

  private enableRecurringSettings(FIELDS) {
    if (FIELDS.paymentScheduleEnabled.value) {
      this.props.changeFields({
        ...FIELDS,
        paymentScheduleEnabled: { ...FIELDS.paymentScheduleEnabled, value: false },
        recurringProfileEnabled: { ...FIELDS.recurringProfileEnabled, value: true },
      })
      this.props.deleteOrder()
    } else {
      this.props.changeFields({
        ...FIELDS,
        recurringProfileEnabled: { ...FIELDS.recurringProfileEnabled, value: true },
      })
    }
    this.props.updateRecurringProfileSettings(getDefaultRecurringProfile(FIELDS.issueDate.value))
  }

  private enableDownpaymentSettings(FIELDS, type: PaymentScheduleType) {
    if (FIELDS.recurringProfileEnabled && FIELDS.recurringProfileEnabled.value) {
      this.props.changeFields({
        ...FIELDS,
        recurringProfileEnabled: { ...FIELDS.recurringProfileEnabled, value: false },
        paymentScheduleEnabled: { ...FIELDS.paymentScheduleEnabled, value: true },
      })
      this.props.deleteRecurringProfileSettings()
    } else {
      this.props.changeFields({
        ...FIELDS,
        paymentScheduleEnabled: { ...FIELDS.paymentScheduleEnabled, value: true },
      })
      InvoiceEditor.scrollIntoView('sumArea')
    }
    this.setDefaultOrder(type, this.props.draft)
  }

  private handleDownPaymentOnChange(FIELDS, enabled: boolean, type: PaymentScheduleType) {
    Analytics.trackEvent(trackingFields.name.activatePaymentSchedule, {
      type: trackingFields.type.action,
      category: trackingFields.category.invoiceSettings,
      result: trackingFields.result.success,
      placement: trackingFields.placement.editor,
      tickbox: enabled,
      selectedOrderType: type,
      draftID: this.props.draft.id,
    })
    if (!enabled) {
      this.props.changeFields({
        ...FIELDS,
        paymentScheduleEnabled: { ...FIELDS.paymentScheduleEnabled, value: false },
      })
      this.props.deleteOrder()
    } else {
      this.enableDownpaymentSettings(FIELDS, type)
      this.setState({ orderType: type })
    }
  }

  private getDownPaymentSettings(FIELDS: DraftFieldsInterface): JSX.Element | '' {
    if ('paymentScheduleEnabled' in FIELDS) {
      return (
        <div>
          <PaymentScheduleSettings
            automation={this.props.automation + '-downPayment'}
            draft={this.props.draft}
            type={'downPayment'}
            enabled={FIELDS.paymentScheduleEnabled.value && this.state.orderType === 'downPayment'}
            onEnabledChange={(enabled: boolean) => {
              Segment.track(SegmentEvents.editorSettings.downPayment.downPaymentCheckboxClicked)
              this.handleDownPaymentOnChange(FIELDS, enabled, 'downPayment')
            }}
          />
          <hr />
          <PaymentScheduleSettings
            automation={this.props.automation + '-paymentSchedule'}
            draft={this.props.draft}
            type={'schedule'}
            enabled={FIELDS.paymentScheduleEnabled.value && this.state.orderType === 'schedule'}
            onEnabledChange={(enabled: boolean) => this.handleDownPaymentOnChange(FIELDS, enabled, 'schedule')}
          />
          <hr />
        </div>
      )
    }
    return ''
  }

  private setDefaultOrder(value: PaymentScheduleType, draft: DraftInterface) {
    if (this.props.hasRightsToPaymentSchedules) {
      const defaultOrder = value === 'schedule' ? getDefaultScheduleOrder(draft) : getDefaultDepositOrder(draft)
      this.props.updateOrder(defaultOrder)
    }
  }

  private renderPaymentScheduleList(order: OrderDraftInterface, dateFormat, language): JSX.Element[] {
    const list: JSX.Element[] = []
    let index = 1
    order.items.forEach((item) => {
      const key = 'paymentScheduleRow-' + index
      list.push(
        <div className={'paymentScheduleRow'} key={key} data-automation={key}>
          <div className={'paymentNumber'}>
            {index++}/{order.items.length}
          </div>
          <div className={'due'}>
            {l('editor.paymentSchedule.due')} {formatDate(item.dueDate, dateFormat, language)}
          </div>
          <div className={'value'}>
            <NumberInput
              decimals={2}
              editable={false}
              borderless={true}
              value={item.value}
              automation={key + '-value'}
              formatter={this.currencyF}
            />
          </div>
        </div>,
      )
    })
    return list
  }

  private renderDownPaymentBlock = (draft: DraftInterface, dateFormat, language) => {
    const order = draft.order || getDefaultDepositOrder(draft)
    const deposit = order.items[0]
    const finalPayment = order.items[1]
    const depositLabel = `${l('editor.downpayment')} ${deposit.percentage ? deposit.percentage + '%' : ''}`
    const depositVatInfo = order.includeVat ? l('editor.downpayment.includesVAT') : l('editor.downpayment.noVAT')
    const depositDueDate = formatDate(deposit.dueDate, dateFormat, language)
    const premiumStar = !doesCurrentPlanSupportFeature(this.props.subscription, QuotaTypes.PAYMENT_SCHEDULE)
    const nonVatTemplate = draft.fields.type.value === 127
    return (
      <div className="downpaymentDeposit" data-automation={this.props.automation + '-downpayment-block'}>
        <hr className="full" />
        <span
          className="delete"
          data-automation={this.props.automation + '-downpayment-delete'}
          onClick={() => this.props.deleteOrder()}
        />
        <NumberInput
          decimals={2}
          borderless={true}
          editable={false}
          label={depositLabel}
          value={deposit.value}
          automation={this.props.automation + '-downpayment'}
          formatter={this.currencyF}
        />
        <div className="depositDue">
          <span className="dueDate">
            {l('editor.downpayment.due')} {depositDueDate}
          </span>
          {!nonVatTemplate && <span className="vatInfo">{depositVatInfo}</span>}
        </div>
        {draft.document === DocumentTypes.INVOICE && (
          <Button
            label={l('editor.downpayment.edit')}
            type={ButtonType.link}
            action={() => {
              Segment.track(SegmentEvents.editorSettings.downPayment.editDownPaymentClicked)
              this.props.openDownpaymentModal()
            }}
            automation={this.props.automation + '-downpayment-editbtn'}
            premium={premiumStar}
          />
        )}
        <hr className="full" />
        <div className="downpaymentFinal">
          <NumberInput
            decimals={2}
            borderless={true}
            editable={false}
            label={l('editor.downpayment.final')}
            value={finalPayment.value}
            automation={this.props.automation + '-downpayment-final'}
            formatter={this.currencyF}
          />
        </div>
      </div>
    )
  }

  private renderPaymentScheduleBlock = (draft: DraftInterface, dateFormat, language) => {
    const order = draft.order || getDefaultScheduleOrder(draft)
    const premiumStar = !doesCurrentPlanSupportFeature(this.props.subscription, QuotaTypes.PAYMENT_SCHEDULE)
    return (
      <div className="paymentSchedule" data-automation={this.props.automation + '-paymentSchedule-block'}>
        <hr className="full" />
        <span
          className="delete"
          data-automation={this.props.automation + '-paymentSchedule-delete'}
          onClick={() => this.props.deleteOrder()}
        />
        <div className="paymentScheduleHeader">{l('editor.paymentSchedule.header')}</div>
        {order.name && <div className="paymentScheduleProject">{order.name}</div>}
        {this.renderPaymentScheduleList(order, dateFormat, language)}
        {draft.document === DocumentTypes.INVOICE && (
          <Button
            label={l('editor.paymentSchedule.edit')}
            type={ButtonType.link}
            action={() => this.props.openPaymentScheduleModal()}
            automation={this.props.automation + '-paymentSchedule-editbtn'}
            premium={premiumStar}
          />
        )}
      </div>
    )
  }

  private getRecurringSettings(FIELDS: DraftFieldsInterface): JSX.Element | '' {
    if ('recurringProfileEnabled' in FIELDS && FIELDS.recurringProfileEnabled.value) {
      return (
        <div
          onClick={(e) =>
            !this.props.hasRightsToRecurringProfile
              ? this.props.openRecurringProfileIntermediaryModal()
              : e.preventDefault()
          }
        >
          <RecurringProfileSettings changeFields={(fields) => this.props.changeFields(fields)} />
        </div>
      )
    } else {
      return ''
    }
  }

  private static scrollIntoView(elClass: string) {
    document.getElementsByClassName(elClass)[0].scrollIntoView({ behavior: 'smooth' })
  }

  private static onChangeScrollToTop() {
    document.getElementsByClassName('editorContent')[0].scrollTo({ top: 0, behavior: 'smooth' })
  }

  private getRecurringToggle(FIELDS: DraftFieldsInterface): JSX.Element | '' {
    if (
      'recurringProfileEnabled' in FIELDS &&
      FIELDS.recurringProfileEnabled.visible &&
      !this.props.draft.id &&
      !this.props.draft.editInvoice &&
      this.props.draft.document === DocumentTypes.INVOICE
    ) {
      return (
        <div className={'RecurringProfileBlock'}>
          <div className={'RecurringProfileToggle'}>
            <CheckBox
              value={FIELDS.recurringProfileEnabled.value}
              label={l(keys.recurring.panelLabel)}
              automation={'RecurringProfileToggle-enabledCheckbox'}
              onChange={(enabled: boolean) => {
                if (enabled) {
                  this.enableRecurringSettings(FIELDS)
                  InvoiceEditor.onChangeScrollToTop()
                  Analytics.trackEvent(Events.toggleMakeRecurring, {
                    type: AnalyticsType.action,
                    category: Category.recurringInvoice,
                    result: Result.success,
                    placement: Placement.editor,
                    status: 'on',
                  })
                } else {
                  if (!this.props.isRecurringProfile) {
                    this.props.changeFields({
                      ...FIELDS,
                      recurringProfileEnabled: { ...FIELDS.recurringProfileEnabled, value: false },
                    })
                    Analytics.trackEvent(Events.toggleMakeRecurring, {
                      type: AnalyticsType.action,
                      category: Category.recurringInvoice,
                      result: Result.success,
                      placement: Placement.editor,
                      status: 'off',
                    })
                    this.props.deleteRecurringProfileSettings()
                  }
                }
              }}
            />
            {doesCurrentPlanSupportFeature(this.props.subscription, QuotaTypes.RECURRING_PROFILE) || (
              <img
                className={'RecurringProfileToggle__star'}
                src={'/images/premiumfeature/star_with_circle_green.svg'}
              />
            )}
          </div>
          <hr />
        </div>
      )
    } else {
      return ''
    }
  }

  private orderActive = (draft: DraftInterface) =>
    draft.fields.paymentScheduleEnabled && draft.fields.paymentScheduleEnabled.value

  private isCompanyInfoFilled = (draft: DraftInterface) => {
    // TODO: what info is necessary?
    return Boolean(draft.invoicer.tradeName && draft.invoicer.tradeName.name)
  }

  private renderInvoicerAddress(address: AddressInterface, key: string) {
    const result = []
    if (address.streetAddress1) {
      result.push(
        <div className="addressLine" key={`invoiceAddress-${result.length}`}>
          {address.streetAddress1}
        </div>,
      )
    }
    if (address.streetAddress2) {
      result.push(
        <div className="addressLine" key={`invoiceAddress-${result.length}`}>
          {address.streetAddress2}
        </div>,
      )
    }
    if (address.city || address.pobox) {
      result.push(
        <div className="addressLine" key={`invoiceAddress-${result.length}`}>
          {address.pobox + ' ' || ''}
          {address.city}
        </div>,
      )
    }
    return (
      <div className="CompanyAddress" key={key}>
        {result}
      </div>
    )
  }

  private renderCompanyInfo = (draft: DraftInterface) => {
    const tradeNameTitles = (originalTradeNames) => {
      const tradeNames = {}
      Object.entries(originalTradeNames).forEach(([key, value], index) => {
        if (value === '') {
          value = `${l('companySettings.createNewTradeName')} ${index + 1}`
        }
        tradeNames[key] = value
      })
      return tradeNames
    }

    const elements: JSX.Element[] = []

    if (!this.isCompanyInfoFilled(draft) && Object.keys(this.props.selectMaps.tradeNames).length <= 1) {
      elements.push(
        <div className="SetCompanyInfoBlock" key={`company-info-${elements.length}`}>
          <div
            className={'SetCompanyInfoBlock__icon'}
            onClick={() => {
              this.props.navigateToCompanySettings?.(draft, TABS.companyDetails, draft.invoicer?.tradeName._id)
              Segment.track(SegmentEvents.experiments.invoiceEditorSettingsChanged, { company_details_added: true })
            }}
          />
          <Button
            type={ButtonType.link}
            automation={'company-info-button'}
            label={l('SetCompanyInfo')}
            action={() => {
              this.props.navigateToCompanySettings?.(draft, TABS.companyDetails, draft.invoicer?.tradeName._id)
              Segment.track(SegmentEvents.experiments.invoiceEditorSettingsChanged, { company_details_added: true })
            }}
          />
        </div>,
      )
    } else {
      if (Object.keys(this.props.selectMaps.tradeNames).length > 1) {
        elements.push(
          <FieldCreator
            name="sender"
            model={this.props.internal.model}
            schema={this.props.internal.schema}
            translator={tsxl}
            key={`company-info-${elements.length}`}
            props={{
              label: null,
              automation: this.props.automation + '-draft-as',
              value: draft.invoicer.tradeName._id,
              searchable: true,
              searchPlaceholder: l('search.Default'),
              map: tradeNameTitles(this.props.selectMaps.tradeNames),
              onChange: (value: string) => {
                this.props.changeDraft({
                  ...draft,
                  invoicer: { ...draft.invoicer, tradeName: { ...draft.invoicer.tradeName, _id: value } },
                })
                Segment.track(SegmentEvents.experiments.invoiceEditorSettingsChanged, { tradename_changed: true })
              },
              borderless: true,
            }}
          />,
        )
      } else {
        elements.push(
          <Input
            editable={false}
            value={draft.invoicer.tradeName.name}
            borderless={true}
            automation={this.props.automation + '-draft-as'}
            key={`company-info-${elements.length}`}
          />,
        )
      }

      if (draft.invoicer.tradeName.address) {
        elements.push(this.renderInvoicerAddress(draft.invoicer.tradeName.address, `company-info-${elements.length}`))
      }

      elements.push(
        <div className={'CompanyProfileLink'} key={`company-info-${elements.length}`}>
          <Button
            key={'editCompanyInfoButton'}
            type={ButtonType.link}
            action={() => {
              this.props.navigateToCompanySettings?.(draft, TABS.companyDetails, draft.invoicer?.tradeName._id)
              Segment.track(SegmentEvents.experiments.invoiceEditorSettingsChanged, { company_details_changed: true })
            }}
            label={tsxl('CompanyInformationLink', this.props.draft.document)}
            automation={this.props.automation + '-company-settings-link'}
          />
        </div>,
      )
    }

    return <div className="CompanyInfo">{elements}</div>
  }

  private renderPersonalisationButton = (draft: DraftInterface) => {
    const elements: JSX.Element[] = []
    if (this.props.customSettings?.customSettings?.name) {
      elements.push(
        <div
          className="InvoiceTemplate"
          onClick={() => {
            this.props.navigateToCompanySettings(draft, TABS.invoiceTemplate)
            Segment.track(SegmentEvents.experiments.invoiceEditorSettingsChanged, { customise_design_viewed: true })
            Analytics.trackEvent(Events.editSettings, {
              type: AnalyticsType.modal,
              category: Category.setting,
              result: Result.open,
              placement: trackingFields.placement.editor,
              tab: 'invoiceTemplate',
              button: 'sideBar',
            })
          }}
        >
          <span>{l('editor.customTemplates.cta.normal')}</span>
        </div>,
      )
    } else {
      elements.push(
        <div className="SetPersonalisationBlock" key={'custom-templates'}>
          <div
            className={'SetPersonalisationBlock__icon'}
            onClick={() => {
              this.props.navigateToCompanySettings(draft, TABS.invoiceTemplate)
              Analytics.trackEvent(Events.editSettings, {
                type: AnalyticsType.modal,
                category: Category.setting,
                result: Result.open,
                placement: trackingFields.placement.editor,
                tab: 'invoiceTemplate',
                button: 'promoCTA',
              })
            }}
          />
          <Button
            type={ButtonType.link}
            automation={'custom-templates-button'}
            label={l('editor.customTemplates.cta.onboarding')}
            action={() => {
              this.props.navigateToCompanySettings(draft, TABS.invoiceTemplate)
              Analytics.trackEvent(Events.editSettings, {
                type: AnalyticsType.modal,
                category: Category.setting,
                result: Result.open,
                placement: trackingFields.placement.editor,
                tab: 'invoiceTemplate',
                button: 'promoCTA',
              })
            }}
          />
        </div>,
      )
    }
    return <div className="CustomTemplates">{elements}</div>
  }

  private renderPaymentMethodsOnboarding = (draft: DraftInterface) => {
    const elements: JSX.Element[] = []
    const paymentMethods =
      (draft.paymentMeans &&
        draft.paymentMeans.filter((p) => {
          return isValidPaymentMethod(p)
        })) ||
      []
    if (paymentMethods.length > 0) {
      return null
    } else {
      elements.push(
        <div className="SetPaymentMethodBlock" key={`payment-method-${elements.length}`}>
          <div
            className={'SetPaymentMethodBlock__icon'}
            onClick={() => {
              this.props.navigateToCompanySettings(draft, TABS.paymentDetails)
              Segment.track(SegmentEvents.experiments.invoiceEditorSettingsChanged, { payment_methods_added: true })
            }}
          />
          <Button
            type={ButtonType.link}
            automation={'payment-method-button'}
            label={l('SetPaymentMeans')}
            action={() => {
              this.props.navigateToCompanySettings(draft, TABS.paymentDetails)
              Segment.track(SegmentEvents.experiments.invoiceEditorSettingsChanged, { payment_methods_added: true })
            }}
          />
        </div>,
      )
    }
    return <div className="PaymentMethods">{elements}</div>
  }

  private renderPaymentMethods = (draft: DraftInterface) => {
    const heading = <div className="Label">{l('PaymentMethods')}</div>
    let contents
    const paymentMeans =
      (draft.paymentMeans &&
        draft.paymentMeans.filter((p) => {
          return isValidPaymentMethod(p)
        })) ||
      []
    if (paymentMeans.length === 0) {
      contents = (
        <div className="SetPaymentMeansBlock">
          <div
            className={'SetPaymentMeansBlock__icon'}
            onClick={() => {
              this.props.navigateToCompanySettings(draft, TABS.paymentDetails)
            }}
          />
          <Button
            type={ButtonType.link}
            automation={'payment-means-button'}
            label={l('SetPaymentMeans')}
            action={() => {
              this.props.navigateToCompanySettings(draft, TABS.paymentDetails)
            }}
          />
        </div>
      )
    } else {
      const paymentMethodOrder = ['iban', 'bban', 'plusgiro', 'bankgiro', 'swish', 'paypal', 'other']
      const schema = this.props.settings.schemas.paymentMethods
      const isMethodEnabled = (pm) => pm && pm.enabled
      const methods: JSX.Element[] = []
      paymentMethodOrder.forEach((pmo) => {
        if (isMethodEnabled(schema[pmo])) {
          if (pmo === 'iban') {
            methods.push(
              <IBANSelector
                key={pmo}
                schema={schema[pmo]}
                paymentMeans={paymentMeans}
                onChange={(ibanFields) => {
                  this.props.updatePaymentMeans(this.props.draft, ibanFields)
                }}
              />,
            )
          } else {
            schema[pmo].fields.forEach((f) => {
              const pm = paymentMeans.find((pm) => pm.type.toLowerCase() === pmo)
              if (pm && pm.fields[f.name]) {
                const pmKey = `${pmo}_${f.name}`
                methods.push(
                  <div className="PaymentMethod" key={pmKey}>
                    {l(f.label)}: {pm.fields[f.name]}
                  </div>,
                )
              }
            })
          }
        }
      })

      contents = (
        <div className="PaymentMethods">
          {methods}
          <Button
            className="EditPaymentMeansButton"
            type={ButtonType.link}
            automation={'payment-means-button'}
            label={l('Edit')}
            action={() => {
              this.props.navigateToCompanySettings(draft, TABS.paymentDetails)
            }}
          />
        </div>
      )
    }
    return (
      <div className="PaymentMeans">
        {heading}
        {contents}
      </div>
    )
  }

  public render(): JSX.Element {
    const DRAFT = this.props.draft
    if (DRAFT === null || DRAFT === undefined || DRAFT.fields === undefined) {
      return null
    }
    const isVATLessSales = this.isVATLessSales()
    const FIELDS = DRAFT.fields
    if (!FIELDS) {
      return <h1>Loading invoice</h1>
    }
    const invoiceTypesMap = { ...this.props.selectMaps.invoiceTypes }

    const defaultProductType = this.getProductTypeFromSettings()

    let discountFormatter: NumberFormatter
    if (FIELDS.discountType.value === 'amount') {
      discountFormatter = new NumberFormatter(
        Format.fromLocale(this.props.token.locale),
        this.props.internal.currencySymbol,
      )
    } else {
      discountFormatter = new PercentageFormatter(Format.fromLocale(FIELDS?.locale?.value), true)
    }

    const recurringEnabled =
      this.props.draft.fields.recurringProfileEnabled && this.props.draft.fields.recurringProfileEnabled.value
    const type = recurringEnabled ? 'recurringProfile' : this.props.draft.document
    const labels = LabelKeys[type]
    const editorContainerClass = 'editorContent'
    const isEdit = Boolean(
      this.props.draft.editInvoice ||
        (this.props.recurringProfile &&
          this.props.recurringProfile.id &&
          this.props.recurringProfile.status &&
          this.props.recurringProfile.status !== RecurringProfileStatusType.DRAFT),
    )
    const saveBtnAutomation = isEdit ? 'header-button-editInvoiceSave' : 'header-button-save'
    const saveBtnLabel = isEdit ? labels.buttons.saveEdit : labels.buttons.save
    const totalLabel = isVATLessSales ? 'it_total_vatless' : 'ToBePaid'
    const dateFormat = transformDateFormatForMoment(this.props.settings?.values?.regional?.longDate)
    const language = this.props.token.locale.split('_')[0]
    const downPaymentClazz = DRAFT.order ? 'downpayment' : 'downpayment downpayment--disabled'
    const activeDepositType =
      (DRAFT.order && DRAFT.order.orderType.startsWith('downPayment')) ||
      (!DRAFT.order && this.state.orderType === 'downPayment')
        ? 'downPayment'
        : 'schedule'
    const pageTitle = isEdit ? l(labels.phases.edit) : l(labels.phases.create)

    const discountEditable = [DocumentTypes.INVOICE, DocumentTypes.ESTIMATE].includes(DRAFT.document)
    return (
      <div className="ReactEditor" data-automation={this.props.automation} style={{ height: '100%' }}>
        <InvoiceEditorHeading
          isAgerasNextMigration={Boolean(this.props.company?.agerasNextMigration)}
          heading={pageTitle}
          closeAction={() => this.props.closeEditor()}
        >
          {this.props.draft.document !== 'creditnote' && (
            <Button
              label={l(saveBtnLabel)}
              automation={saveBtnAutomation}
              action={() => {
                !this.props.hasRightsToRecurringProfile && FIELDS.recurringProfileEnabled.value
                  ? this.props.openRecurringProfileIntermediaryModal()
                  : this.props.draftSaveAndClose?.(this.props.draft)
              }}
            />
          )}
          <Button
            label={l(labels.buttons.next)}
            automation="header-button-next"
            type={ButtonType.accept}
            className="editor"
            action={() => {
              // eslint-disable-next-line no-nested-ternary
              !this.props.hasRightsToRecurringProfile && FIELDS.recurringProfileEnabled.value
                ? this.props.openRecurringProfileIntermediaryModal()
                : FIELDS.recurringProfileEnabled.value
                  ? this.props.previewRecurringProfile(this.props.draft, this.props.recurringProfile)
                  : this.props.previewDraft?.(this.props.draft)
            }}
          />
        </InvoiceEditorHeading>
        <div className={editorContainerClass}>
          <Layout>
            <LayoutColumn style={{ flexGrow: 3 }}>
              {this.getRecurringSettings(FIELDS)}
              <Document automation={this.props.automation} className="InvoiceEditor">
                <div className="fadeInContent">
                  <h1 className="title">{FIELDS.title.value}</h1>
                  <CustomerSelector
                    customers={this.props.customers.collection}
                    documentType={this.props?.draft?.document}
                    highlight={this.state.highlightCustomerSelection}
                    onClickOutside={() => {
                      if (this.state.highlightCustomerSelection) {
                        this.setState({ highlightCustomerSelection: false })
                      }
                    }}
                    value={DRAFT.receiver}
                    onChange={(customer) => {
                      let { invalidFields = [] } = this.props.draft
                      if (customer && invalidFields.includes('customer')) {
                        invalidFields = invalidFields.filter((item) => item !== 'customer')
                        this.props.changeDraft({ ...this.props.draft, invalidFields })
                      }
                      this.props.changeCustomer(customer)
                    }}
                    containerClass={editorContainerClass}
                    invalid={this.props.draft.invalidFields && this.props.draft.invalidFields.indexOf('customer') >= 0}
                    readOnly={this.props.draft.document === 'creditnote' || this.isEditingApprovedInvoice()}
                    openCustomerModal={(customerName) => {
                      this.props.openCustomerModal(DRAFT.receiver, customerName)
                    }}
                  />
                  <hr />
                  <InvoiceHeader
                    onChange={(field: FieldInterface) => {
                      this.props.draft.fields[field?.name?.toString()] = field
                      this.props.changeFields({ ...this.props.draft.fields })
                    }}
                    settings={this.props.settings}
                    model={this.props.internal.model}
                    schema={this.props.internal.schema}
                    fields={this.props.draft.fields}
                    invalidFields={this.props.draft.invalidFields}
                    eInvoiceCharacterLimit={
                      this.props.draft?.characterLimitReached?.description && this.props.draft?.eInvoiceSending
                    }
                    automation={'invoiceHeader'}
                    locale={this.props.token.locale}
                    country={this.props.company.country}
                    onDocumentNumberEdit={this.props.onDocumentNumberEdit}
                    editableInvoiceNumberInput={this.props.draft.document === DocumentTypes.ESTIMATE}
                    invoiceNumberEditable={
                      (this.props.draft.document === 'invoice' && !this.isEditingApprovedInvoice()) ||
                      this.props.draft.document === 'creditnote'
                    }
                    openPaymentTermsModal={this.props.openPaymentTermsModal}
                    openLatePaymentInterestModal={this.props.openLatePaymentInterestModal}
                  />
                  <InvoiceItems
                    document={this.props.draft.document}
                    products={this.props.products}
                    lines={DRAFT.lines}
                    settings={this.props.settings}
                    fields={this.props.draft.fields}
                    columns={this.props.internal.lineColumns as Array<keyof LineInterface>}
                    defaultProductType={defaultProductType}
                    externalVATRates={this.props.externalVATRates}
                    isAgerasNextMigration={this.props.isAgerasNextMigration}
                    customerType={
                      this.props.draft?.receiver?.customerType &&
                      CUSTOMER_TYPE_TO_CONTACT_TYPE[this.props.draft?.receiver?.customerType]
                    }
                    customerCountry={this.props.draft?.receiver?.address?.country}
                    onChange={(values, action, fieldName, index) => {
                      if (
                        this.props.company?.agerasNextMigration &&
                        action === 'change' &&
                        index !== undefined &&
                        values[index].type === 'row' &&
                        (fieldName === 'productType' ||
                          (fieldName === 'description' && !(values[index] as LineInterface).vatRateId))
                      ) {
                        const lineValues = values[index] as LineInterface
                        const { productType, vatRateId } = lineValues

                        if (this.shouldSetVatRateFromRules(productType, vatRateId)) {
                          const vatRateIdFromRules = this.getVatRateFromRules(productType)

                          if (vatRateIdFromRules) {
                            lineValues.vatRateId = vatRateIdFromRules
                          }
                        }
                      }

                      let { invalidFields = [] } = this.props.draft

                      if (values && Array.isArray(values)) {
                        values.forEach((value, index) => {
                          if (value.type === 'row') {
                            if (!Object.values(ProductTypesEnum).includes(value.productType)) {
                              value.productType = defaultProductType || ProductTypesEnum.goods
                            }

                            const fieldName = `line-${index}-productType`

                            if (value.productType && invalidFields.includes(fieldName)) {
                              invalidFields = invalidFields.filter((item) => item !== fieldName)
                            }
                          }
                        })

                        if (this.props.draft.invalidFields !== invalidFields) {
                          this.props.changeDraft({ ...this.props.draft, invalidFields })
                        }
                      }

                      this.props.changeLines(values)
                    }}
                    locale={this.props.token?.locale}
                    currencySymbol={this.props.internal.currencySymbol}
                    readOnly={this.props.draft.document === 'creditnote'}
                    openProductModal={(index) => this.props.openProductModal(index, this.props.draft?.document)}
                    openDefaultProductTypeModal={(index, onClose) => {
                      if (this.props.openDefaultProductTypeModal) {
                        this.props.openDefaultProductTypeModal(index, onClose)
                      }
                    }}
                    openVATOptions={this.props.openVATOptions}
                    openUnitOptions={this.props.openUnitOptions}
                    eInvoiceSending={this.props.draft?.eInvoiceSending}
                    eInvoiceCharacterLimit={this.props.draft?.characterLimitReached}
                    invalidFields={this.props.draft.invalidFields}
                  />

                  <Layout>
                    <LayoutColumn>
                      {this.props.draft.document !== 'creditnote' && (
                        <Button
                          className="addItemButton"
                          label={tsxl('New')}
                          type={ButtonType.accept}
                          action={() => {
                            const defaultLineValues = this.getDefaultLineValues()
                            this.props.draft.lines.push(defaultLineValues)
                            this.props.changeLines(this.props.draft.lines)
                          }}
                          automation="invoiceItem-addLine"
                        >
                          <Button
                            label={tsxl('New')}
                            type={ButtonType.accept}
                            action={() => {
                              const defaultLineValues = this.getDefaultLineValues()
                              this.props.draft.lines.push(defaultLineValues)
                              this.props.changeLines(this.props.draft.lines)
                            }}
                            automation="invoiceItem-addLine"
                          />
                          <Button
                            label={tsxl('invoice.add.Subtitle')}
                            type={ButtonType.accept}
                            action={() => {
                              Segment.track(SegmentEvents.invoice.invoiceFieldsFilled, { add_title_clicked: true })
                              this.props.draft.lines.push(InvoiceTitleLine.defaultProps)
                              this.props.changeLines(this.props.draft.lines)
                            }}
                            automation="invoiceItem-addTitle"
                          />
                          <Button
                            label={tsxl('invoice.add.Description')}
                            type={ButtonType.accept}
                            action={() => {
                              Segment.track(SegmentEvents.invoice.invoiceFieldsFilled, { add_text_field_clicked: true })
                              // @ts-ignore
                              const o: TextLineInterface = {
                                ...InvoiceTextLine.defaultProps,
                                content: '',
                                type: 'description',
                              }
                              this.props.draft.lines.push(o)
                              this.props.changeLines(this.props.draft.lines)
                            }}
                            automation="invoiceItem-addDescription"
                          />
                          <Button
                            label={tsxl('invoice.add.Subtotal')}
                            type={ButtonType.accept}
                            action={() => {
                              Segment.track(SegmentEvents.invoice.invoiceFieldsFilled, { add_subtotal_clicked: true })
                              const { lineColumns } = this.props.internal
                              const isNetTotal = lineColumns.indexOf('netTotal') >= 0
                              const isTaxTotal = lineColumns.indexOf('taxTotal') >= 0
                              this.props.draft.lines.push(SubtotalLine.generateDefaultProps(isNetTotal, isTaxTotal))
                              this.props.changeLines(this.props.draft.lines)
                            }}
                            automation="invoiceItem-addSubtotal"
                          />
                        </Button>
                      )}
                    </LayoutColumn>
                    <LayoutColumn style={{ flexBasis: '300px', flexGrow: 0 }}>
                      <div className="sumArea">
                        {FIELDS.discountEnabled.value && FIELDS.discountLocation.value === 'perTotal' && (
                          <NumberInput
                            decimals={2}
                            key={'amount'}
                            formatter={discountFormatter}
                            automation={this.props.automation + '-discount'}
                            value={FIELDS.discountAmount.value}
                            label={tsxl('DiscountGiven', this.props.draft.document)}
                            onBlur={(value) => {
                              this.props.changeFields({
                                ...FIELDS,
                                discountAmount: { name: 'discountAmount', value },
                              })
                            }}
                            editable={discountEditable}
                            borderless={!discountEditable}
                          />
                        )}
                        {FIELDS.discountEnabled.value && FIELDS.discountLocation.value === 'perTotal' && (
                          <hr className="full" />
                        )}
                        {FIELDS.discountEnabled.value &&
                          FIELDS.discountLocation.value === 'perTotal' &&
                          FIELDS.discountAmount.value > 0 && (
                            <NumberInput
                              decimals={2}
                              editable={false}
                              borderless={true}
                              value={DRAFT.summary.subTotal}
                              automation={this.props.automation + '-sub'}
                              formatter={this.currencyF}
                              label={
                                FIELDS.discountVAT.value === 'postVAT'
                                  ? tsxl('invoice.subTotalInclVAT')
                                  : tsxl('invoice.subTotal')
                              }
                            />
                          )}
                        {FIELDS.discountEnabled.value && FIELDS.discountLocation.value === 'perTotal' && (
                          <NumberInput
                            decimals={2}
                            borderless={true}
                            editable={false}
                            formatter={this.currencyF}
                            value={this.props.draft.summary.discountTotal}
                            automation={this.props.automation + '-discountGiven'}
                            label={`${l('DiscountGiven', this.props.draft.document)}
                            ${
                              FIELDS.discountType.value === 'amount'
                                ? ''
                                : convertPercentageFromLocale(FIELDS.discountAmount.value, this.props.token.locale)
                            }`}
                          />
                        )}
                        {FIELDS.discountEnabled.value && FIELDS.discountLocation.value === 'perLine' && (
                          <NumberInput
                            decimals={2}
                            borderless={true}
                            editable={false}
                            formatter={this.currencyF}
                            value={this.props.draft.summary.discountTotal}
                            automation={this.props.automation + '-discountGiven'}
                            label={`${l('DiscountGiven', this.props.draft.document)}`}
                          />
                        )}
                        {FIELDS.discountEnabled.value && <hr className="full" />}
                        {!isVATLessSales && (
                          <NumberInput
                            decimals={2}
                            editable={false}
                            borderless={true}
                            value={DRAFT.summary.netTotal}
                            automation={this.props.automation + '-net'}
                            formatter={this.currencyF}
                            label={tsxl('invoice.totalAmount')}
                          />
                        )}
                        {!this.props.internal.taxFree &&
                          DRAFT.summary.taxSummary.map((vat) => {
                            return (
                              vat.percentage > 0 && (
                                <NumberInput
                                  decimals={2} // do not show VAT 0% as it is always 0
                                  formatter={this.currencyF}
                                  editable={false}
                                  key={'vat' + vat.percentage}
                                  automation={this.props.automation + '-vat-' + vat.percentage}
                                  borderless={true}
                                  label={l('Tax') + ' ' + convertPctFromLocale(vat.percentage, this.props.token.locale)}
                                  value={vat.amount}
                                />
                              )
                            )
                          })}
                        {!this.props.internal.taxFree && DRAFT.summary.taxSummary.length > 1 && (
                          <NumberInput
                            decimals={2}
                            borderless={true}
                            editable={false}
                            value={DRAFT.summary.taxTotal}
                            automation={this.props.automation + '-vat'}
                            formatter={this.currencyF}
                            label={tsxl('invoice.VatTotal')}
                          />
                        )}
                        {[InvoiceTypes.SALES_HOUSEHOLD_SE_ROT, InvoiceTypes.SALES_HOUSEHOLD_SE_RUT].includes(
                          Number(FIELDS.type.value),
                        ) &&
                          !!DRAFT.summary.reductionTotal && [
                            <div className="sum">
                              <NumberInput
                                decimals={2}
                                borderless={true}
                                editable={false}
                                value={DRAFT.summary.grossTotal}
                                automation={this.props.automation + '-sum'}
                                formatter={this.currencyF}
                                label={tsxl('Sum')}
                              />
                            </div>,
                            <div className="reduction">
                              <NumberInput
                                decimals={2}
                                borderless={true}
                                editable={false}
                                value={-1 * DRAFT.summary.reductionTotal}
                                automation={this.props.automation + '-deductionTotal'}
                                formatter={this.currencyF}
                                label={tsxl(
                                  FIELDS.type.value === 10
                                    ? 'invoice.total.SALES_HOUSEHOLD_SE_ROT'
                                    : 'invoice.total.SALES_HOUSEHOLD_SE_RUT',
                                )}
                              />
                            </div>,
                          ]}
                        <hr className="full" />
                        <div className="total">
                          <NumberInput
                            decimals={2}
                            borderless={true}
                            editable={false}
                            value={
                              [10, 11].includes(Number(FIELDS.type.value))
                                ? DRAFT.summary.grossTotal - DRAFT.summary.reductionTotal
                                : DRAFT.summary.grossTotal
                            }
                            automation={this.props.automation + '-total'}
                            formatter={this.currencyF}
                            label={tsxl(totalLabel)}
                          />
                        </div>
                      </div>
                      {this.orderActive(DRAFT) && (
                        <div className={downPaymentClazz}>
                          {activeDepositType === 'downPayment'
                            ? this.renderDownPaymentBlock(DRAFT, dateFormat, language)
                            : this.renderPaymentScheduleBlock(DRAFT, dateFormat, language)}
                        </div>
                      )}
                    </LayoutColumn>
                  </Layout>
                  <hr />
                  <div className="footer">
                    <Layout>
                      <LayoutColumn>
                        <FieldCreator
                          name="footerText"
                          model={this.props.internal.model}
                          schema={this.props.internal.schema}
                          translator={tsxl}
                          props={{
                            className: ['invoiceEditor-footer'],
                            value: FIELDS.footerText ? FIELDS.footerText.value : '',
                            labelPosition: 'top',
                            automation: this.props.automation + '-footnote',
                            invalid:
                              this.props.draft.invalidFields &&
                              this.props.draft.invalidFields.indexOf('footerText') >= 0,
                            onBlur: (value: string) => {
                              this.props.changeFields({
                                ...FIELDS,
                                footerText: { ...FIELDS.footerText, value },
                              })
                            },
                            editable: this.props.draft.document !== 'creditnote',
                            borderless: this.props.draft.document === 'creditnote',
                            maxLength: 250,
                            showCounter: true,
                          }}
                        />
                      </LayoutColumn>
                      <LayoutColumn>
                        {this.props.draft.document !== 'creditnote' &&
                          this.props.draft.document !== DocumentTypes.ESTIMATE &&
                          this.renderPaymentMethods(DRAFT)}
                      </LayoutColumn>
                    </Layout>
                  </div>
                </div>
              </Document>
            </LayoutColumn>
            <LayoutColumn style={{ flexGrow: 0, flexBasis: '240px' }}>
              {this.props.draft.document !== 'creditnote' && (
                <Document type="Panel" className={'sidePanel'}>
                  <div className="fadeInContent">
                    {this.renderCompanyInfo(DRAFT)}
                    {this.renderPaymentMethodsOnboarding(DRAFT)}
                    {this.renderPersonalisationButton(DRAFT)}
                    <InvoiceSettings
                      {...this.props}
                      handleCurrencyChange={this.handleCurrencyChange}
                      handleExchangeRateChange={this.handleExchangeRateChange}
                    />
                    <div>
                      <DiscountSettings
                        label={l('discountSettings', this.props.draft.document)}
                        automation={this.props.automation + '-discount'}
                        discountLocation={
                          [InvoiceTypes.SALES_HOUSEHOLD_SE_ROT, InvoiceTypes.SALES_HOUSEHOLD_SE_RUT].includes(
                            FIELDS.type.value,
                          )
                            ? 'perLine'
                            : FIELDS.discountLocation.value
                        }
                        discountLocationValue={this.props?.draft?.fields?.discountLocation?.value}
                        discountVATValue={this.props?.draft?.fields?.discountVAT?.value}
                        discountType={FIELDS.discountType.value}
                        discountVAT={FIELDS.discountVAT.value}
                        discountState={FIELDS.discountEnabled.value ? 'on' : 'off'}
                        currency={FIELDS.currency.value}
                        discountLocationsMap={{
                          ...(![InvoiceTypes.SALES_HOUSEHOLD_SE_ROT, InvoiceTypes.SALES_HOUSEHOLD_SE_RUT].includes(
                            FIELDS.type.value,
                          )
                            ? {
                                perTotal: !(
                                  this.props.draft.fields.discountVAT.value === 'preVAT' &&
                                  this.props.draft.fields.discountLocation.value === 'perTotal' &&
                                  this.props.draft?.invalidFields?.includes('discount')
                                )
                                  ? l('document.perTotal')
                                  : tsxl('document.perTotal.xrechnung.fail'),
                              }
                            : {}),
                          perLine: !(
                            this.props.draft.fields.discountVAT.value === 'postVAT' &&
                            this.props.draft.fields.discountLocation.value === 'perLine' &&
                            this.props.draft?.invalidFields?.includes('discount')
                          )
                            ? l('document.perLine')
                            : tsxl('document.perLine.xrechnung.fail'),
                        }}
                        onDiscountStateChange={(discountState: DiscountState) => {
                          const amount = discountState === 'on' ? FIELDS.discountAmount.value : 0
                          this.props.changeFields({
                            ...FIELDS,
                            discountEnabled: { name: 'discountEnabled', value: discountState === 'on' },
                            discountAmount: { name: 'discountAmount', value: amount },
                          })
                          if (discountState === 'off') {
                            DRAFT.lines.forEach((line: InvoiceItemType) => {
                              if (Type.isInvoiceLine(line)) {
                                if (line.discount > 0) {
                                  line.discount = 0
                                  line.totalDiscount = 0
                                }
                              }
                            })
                            this.props.changeLines(DRAFT.lines)
                          }
                        }}
                        onDiscountLocationChange={(discountLocation: DiscountLocation) => {
                          this.props.changeFields({
                            ...FIELDS,
                            discountLocation: { name: 'discountLocation', value: discountLocation },
                          })
                        }}
                        onDiscountTypeChange={(discountType: DiscountType) => {
                          this.props.changeFields({
                            ...FIELDS,
                            discountType: { name: 'discountType', value: discountType },
                          })
                        }}
                        onDiscountVATChange={(discountVAT: DiscountVAT) => {
                          this.props.changeFields({
                            ...FIELDS,
                            discountVAT: { ...FIELDS.discountVAT, value: discountVAT },
                          })
                        }}
                        showDiscountVAT={this.props.selectMaps.columnNames.taxTotal !== undefined}
                        blockedXrechung={this.props.draft?.invalidFields?.includes('discount')}
                      />
                    </div>
                    {this.getRecurringToggle(FIELDS)}
                    {!this.props.isRecurringProfile && this.getDownPaymentSettings(FIELDS)}
                  </div>
                </Document>
              )}
              {this.props.draft.document === 'creditnote' && (
                <div className="fadeInContent">
                  <Document type="Panel">
                    <Input
                      automation={this.props.automation + '-draft-as'}
                      value={this.props.selectMaps.tradeNames[DRAFT.invoicer.tradeName._id]}
                      editable={false}
                      borderless={true}
                      label={l('invoice.InvoicingProfile')}
                    />
                    <hr />

                    <Input
                      automation={this.props.automation + '-invoice-type'}
                      value={this.props.selectMaps.invoiceTypes[FIELDS.type.value]}
                      editable={false}
                      borderless={true}
                      label={l('invoice.Type')}
                    />
                    <hr />

                    <Input
                      automation={this.props.automation + '-template'}
                      value={this.props.selectMaps.templates[this.props.internal.template]}
                      editable={false}
                      borderless={true}
                      label={l('invoice.Template')}
                    />
                    <hr />

                    <Input
                      automation={this.props.automation + '-visible-columns'}
                      value={this.props.internal.visibleColumns
                        .map((key) => {
                          return this.props.selectMaps.columnNames[key]
                        })
                        .filter((i) => i)
                        .join(', ')}
                      dataValue={this.props.internal.visibleColumns.join(',')}
                      editable={false}
                      borderless={true}
                      label={l('invoice.Fields')}
                    />
                    <hr />

                    <Input
                      automation={this.props.automation + '-currency'}
                      value={this.props.selectMaps.currencies[FIELDS.currency.value]}
                      editable={false}
                      borderless={true}
                      label={l('Currency')}
                    />
                    <hr />

                    <Input
                      automation={this.props.automation + '-language'}
                      value={this.props.selectMaps.languages[this.props.internal.language]}
                      editable={false}
                      borderless={true}
                      label={l('InvoiceLanguage')}
                    />
                    <hr />

                    <Input
                      automation={this.props.automation + '-title'}
                      value={FIELDS.title.value}
                      editable={false}
                      borderless={true}
                      label={l('invoice.Title')}
                    />
                    <hr />
                    <div className="Controlled Input SettingsLink">
                      <span className="Label" onClick={() => this.props.navigateToInvoicingSettings()}>
                        {tsxl('AdvancedSettings', this.props.draft.document)}
                      </span>
                    </div>
                  </Document>
                </div>
              )}
            </LayoutColumn>
          </Layout>
        </div>
      </div>
    )
  }
}
